import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StructuredData from '../components/StructuredData';
import { ArrowRight, CheckCircle, Star } from 'lucide-react';
import { services, testimonials, stats } from '../mockData';
import './Home.css';

const Home = () => {
  return (
    <div className="home-page">
      <Helmet>
        <title>Kuşadası Kombi Servisi | Söke Beyaz Eşya Tamiri | Aydın Klima Montaj - teknikservisburda.com</title>
        <meta name="description" content="Kuşadası, Söke ve Aydın'da güvenilir kombi servisi, beyaz eşya tamiri ve klima montaj hizmeti. 15 yıllık tecrübe, aynı gün servis. Hemen arayın: 0551 847 8521" />
        <meta name="keywords" content="Kuşadası kombi servisi, Söke kombi tamircisi, Aydın beyaz eşya servisi, Kuşadası klima montaj, buzdolabı tamiri, çamaşır makinesi servisi" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://teknikservisburda.com/" />
        <meta property="og:title" content="Kuşadası Kombi Servisi | Söke Beyaz Eşya | Aydın Klima - teknikservisburda.com" />
        <meta property="og:description" content="Kuşadası, Söke ve Aydın'da profesyonel kombi, beyaz eşya ve klima servisi. Aynı gün servis garantisi." />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_ev-aletleri-servis/artifacts/vs6pzaqs_1.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kuşadası Kombi Servisi | Söke Beyaz Eşya | Aydın Klima" />
        <meta name="twitter:description" content="15 yıllık tecrübe ile profesyonel teknik servis. Aynı gün servis garantisi." />
        
        {/* Additional SEO */}
        <meta name="geo.region" content="TR-09" />
        <meta name="geo.placename" content="Kuşadası, Söke, Aydın" />
        <meta name="geo.position" content="37.8586;27.2597" />
        <link rel="canonical" href="https://teknikservisburda.com/" />
      </Helmet>
      
      <StructuredData />
      
      <Header />
      
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title fade-in">
              Kuşadası, Söke ve Aydın'da Güvenilir Teknik Servis Hizmeti
            </h1>
            <p className="hero-subtitle fade-in">
              15 yıllık tecrübemizle <strong>kombi bakım ve onarım</strong>, <strong>beyaz eşya tamiri</strong> ve <strong>klima montaj</strong> hizmetleri sunuyoruz. 
              Aynı gün servis garantisi ile Kuşadası, Söke, Aydın ve çevre ilçelerde yanınızdayız.
            </p>
            <div className="hero-buttons fade-in">
              <Link to="/talep" className="btn-primary">
                Ücretsiz Teklif Alın
                <ArrowRight size={20} />
              </Link>
              <a href="tel:+905518478521" className="btn-secondary">
                Hemen Ara: 0551 847 8521
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <h3 className="stat-value">{stat.value}</h3>
                <p className="stat-label">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Kuşadası, Söke ve Aydın'da Sunduğumuz Hizmetler</h2>
            <p className="text-muted">Tüm markalarda profesyonel tamir ve bakım hizmetleri</p>
          </div>
          
          {/* Video Introduction Section */}
          <div className="video-intro-section">
            <div className="video-container">
              {/* YouTube video embed - replace VIDEO_ID with actual video */}
              <div className="video-placeholder" id="intro-video">
                <div className="video-overlay">
                  <button className="play-button" onClick={() => {
                    // Video yüklendiğinde bu placeholder'ı gerçek video ile değiştirebilirsiniz
                    alert('Tanıtım videomuz yakında eklenecek!');
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                  </button>
                  <p className="video-label">Tanıtım Videomuzu İzleyin</p>
                </div>
              </div>
            </div>
            <div className="video-description">
              <h3>Neden Bizi Tercih Etmelisiniz?</h3>
              <ul>
                <li><CheckCircle size={18} /> <strong>15 yıllık tecrübe</strong> ile güvenilir hizmet</li>
                <li><CheckCircle size={18} /> <strong>Aynı gün servis</strong> garantisi</li>
                <li><CheckCircle size={18} /> <strong>Orijinal yedek parça</strong> kullanımı</li>
                <li><CheckCircle size={18} /> <strong>6-12 ay garanti</strong> tüm işlemlerde</li>
              </ul>
            </div>
          </div>
          
          <div className="services-grid">
            {services.map((service) => (
              <div key={service.id} className="service-card card">
                <div className="service-icon">
                  <CheckCircle size={32} />
                </div>
                <h3>{service.name}</h3>
                <p className="text-muted">{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, index) => (
                    <li key={index}>
                      <CheckCircle size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/talep" className="service-link">
                  Talep Oluştur <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-us-section bg-light section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Neden Bizi Seçmelisiniz?</h2>
            <p className="text-muted">Müşteri memnuniyeti odaklı profesyonel hizmet anlayışımız</p>
          </div>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">
                <CheckCircle size={28} />
              </div>
              <h3>Hızlı Servis</h3>
              <p>Aynı gün içinde servis hizmeti</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <CheckCircle size={28} />
              </div>
              <h3>Uzman Kadro</h3>
              <p>Sertifikalı ve deneyimli teknisyenler</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <CheckCircle size={28} />
              </div>
              <h3>Garantili Hizmet</h3>
              <p>Tüm işlemlerimize garanti</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <CheckCircle size={28} />
              </div>
              <h3>Uygun Fiyat</h3>
              <p>Rekabetçi ve şeffaf fiyatlandırma</p>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="testimonials-section section">
        <div className="container">
          <div className="section-header text-center">
            <div className="google-badge">
              <img 
                src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
                alt="Google" 
                className="google-logo"
              />
              <div className="rating-badge">
                <span className="rating-value">4.8</span>
                <div className="rating-stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="#FBBC04" color="#FBBC04" />
                  ))}
                </div>
                <span className="review-count">156 yorum</span>
              </div>
            </div>
            <h2>Google'da Müşteri Yorumlarımız</h2>
            <p className="text-muted">Müşterilerimizin Google'da bizim hakkımızda söyledikleri</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card card">
                <div className="testimonial-header">
                  <div className="testimonial-avatar">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div className="testimonial-info">
                    <p className="author-name">{testimonial.name}</p>
                    <div className="testimonial-rating">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} size={14} fill="#FBBC04" color="#FBBC04" />
                      ))}
                    </div>
                  </div>
                  <img 
                    src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" 
                    alt="Google" 
                    className="google-icon"
                  />
                </div>
                <p className="testimonial-text">"{testimonial.comment}"</p>
                <p className="author-service">{testimonial.service}</p>
              </div>
            ))}
          </div>
          <div className="google-review-cta">
            <a 
              href="https://search.google.com/local/writereview?placeid=YOUR_PLACE_ID" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-google-review"
            >
              <Star size={20} />
              Google'da Yorum Yapın
            </a>
            <a 
              href="https://search.google.com/local/reviews?placeid=YOUR_PLACE_ID" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-view-reviews"
            >
              Tüm Yorumları Gör
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Hemen Servis Talebi Oluşturun</h2>
            <p>7/24 müşteri desteğimiz ile yanınızdayız</p>
            <Link to="/talep" className="btn-primary">
              Talep Formu <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;